import React, { Component, ErrorInfo, ReactElement, ReactNode } from 'react';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import { BrowserRouter as Router } from 'react-router-dom';
import { ErrorScreen, StyleSheetManager, theme } from '@mesa-labs/mesa-ui';
import { DateTime } from 'luxon';

import Routes from '../routes';
import useUserIdleTimeout from '../hooks/useUserIdleTimeout';
import { useSelector } from '../redux/hooks';
import IdleTimeoutWarningModal from './common/IdleTimeoutWarningModal';
import FourTwoNineErrorPage from './common/429ErrorPage';

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorScreen onClick={() => window.location.reload()} />;
    }

    return this.props.children;
  }
}

const GlobalStyle = createGlobalStyle`
  html, body, #root {
    height: 100%;
  }

  html {
    overflow-y: scroll;
  }

  #modal-root {
    position: fixed;
    z-index: 110;
  }

  body {
    font-family: ${(props) => props.theme.fonts.inter};
    padding: 0px;
    margin: 0px;

    input:focus {
      outline: none;
    }
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type=number] {
    -moz-appearance: textfield;
    appearance: textfield;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  * {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;

    box-sizing: border-box;
    margin: 0px;
    padding: 0px;

    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.15) transparent;

    &::-webkit-scrollbar {
      width: 4px;
      height: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.15);
      border-radius: 4px;

      &:hover {
        background: rgba(0, 0, 0, 0.25);
      }
    }
  }

  .pac-container {
    border: 1px solid rgba(0, 0, 0, .2);
    border-radius: 4px;
    box-shadow: none;
    filter: drop-shadow(0px 4px 20px rgba(0, 0, 0, 0.1));
    margin-top: 4px;

    .pac-item {
      border: none;
      padding: 4px;
    }
  }
`;

const Layout = styled.div`
  font-size: 16px;
  height: 100%;
`;

function App(): ReactElement {
  useUserIdleTimeout();

  const showIdleTimeoutWarningModal = useSelector((state) => state.modals.showIdleTimeoutWarningModal);
  const timeout429Timestamp = useSelector((state) => state.auth.timeout429Timestamp) || 0;

  if (timeout429Timestamp > 0) {
    if (timeout429Timestamp > DateTime.now().minus({ seconds: 60 }).toMillis()) {
      return (
        <StyleSheetManager>
          <ThemeProvider theme={theme}>
            <GlobalStyle />
            <Layout id="mesa-ui-429">
              <FourTwoNineErrorPage />
            </Layout>
          </ThemeProvider>
        </StyleSheetManager>
      );
    }
  }

  return (
    <StyleSheetManager>
      <ThemeProvider theme={theme}>
        <Layout id="mesa-ui">
          <Router>
            <IdleTimeoutWarningModal open={showIdleTimeoutWarningModal} />
            <GlobalStyle />
            <ErrorBoundary>
              <Routes />
            </ErrorBoundary>
          </Router>
        </Layout>
      </ThemeProvider>
    </StyleSheetManager>
  );
}

export default App;
